/**
 * RiderAccountingPage Component
 * 라이더 출금제한 관리 페이지
 */
import React, { useState, useMemo, useCallback } from 'react';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import { useTableSort } from '@/shared/hooks/useTableSort';
import { useAccountingUtils } from '../hooks/useAccountingUtils';
import { useRiderData } from '../hooks/useRiderData';
import { RiderListItem } from '../types/riderAccounting.types';
import FilterSection from './FilterSection';
import RiderTableRow from './RiderTableRow';
import '../styles/AccountingRider.css';

/**
 * 테이블 헤더 컬럼 정의
 */
const TABLE_HEADERS = [
    { key: 'suppName', label: '협력사명', sortable: false },
    { key: 'riderName', label: '라이더명', sortable: false },
    { key: 'contStatus', label: '계약상태', sortable: false },
    { key: 'riderId', label: '라이더ID', sortable: false },
    { key: 'preAcctYn', label: '선정산', sortable: false },
    { key: 'riderInsDate', label: '등록일자', sortable: true },
    { key: 'contEdDate', label: '계약종료일자', sortable: true },
    { key: 'balAmt', label: '잔액', sortable: true },
    { key: 'unpaidAmt', label: '미납금', sortable: true },
    { key: 'action', label: '계좌관리', sortable: false }
] as const;

const RiderAccountingPage: React.FC = () => {
    // ========================================
    // Hooks
    // ========================================
    const {
        searchRiderName,
        setSearchRiderName,
        searchRiderId,
        setSearchRiderId,
        searchContractStatus,
        setSearchContractStatus,
        searchPartnerId,
        setSearchPartnerId,
        searchWithdrawalStatus,
        setSearchWithdrawalStatus,
        allData,
        loading,
        userType,
        contractStatusCodes,
        contractStatusLoading,
        partnerNames,
        itemsPerPage,
        handleSearch,
        handleWithdrawalLimit
    } = useRiderData();

    const {
        getRiderContractStatusBadge,
        formatNumber,
        handleSendMoney,
        getRiderContractStatusBadgeClass,
        getPreAccountBadgeClass,
        getPreAccountBadgeText,
        getRiderRowClassName,
        isRiderAccountDisabled
    } = useAccountingUtils();

    // 정렬 훅 사용
    const {
        sortConfig,
        sortedData: sortedAllData,
        handleSort: onSort
    } = useTableSort<RiderListItem>({
        data: allData || [],
        numericColumns: ['balAmt', 'unpaidAmt'],
        dateColumns: ['riderInsDate', 'contEdDate']
    });

    // 페이지 상태
    const [currentPage, setCurrentPage] = useState(1);

    // 정렬 핸들러 (정렬 시 첫 페이지로 이동)
    const handleSort = useCallback((columnKey: string) => {
        onSort(columnKey);
        setCurrentPage(1);
    }, [onSort]);

    // 페이지네이션 적용된 현재 페이지 데이터
    const currentPageData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedAllData.slice(startIndex, endIndex);
    }, [sortedAllData, currentPage, itemsPerPage]);

    // 전체 페이지 수
    const totalPages = useMemo(() => {
        return Math.ceil(sortedAllData.length / itemsPerPage);
    }, [sortedAllData.length, itemsPerPage]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    // 검색 시 첫 페이지로 리셋
    const handleSearchWithReset = useCallback(() => {
        setCurrentPage(1);
        handleSearch();
    }, [handleSearch]);

    // ========================================
    // Render - Loading State
    // ========================================
    if (loading) {
        return (
            <div className="account-rider">
                <div className="loading-container">
                    <Loading message="라이더 정보를 불러오는 중..." />
                </div>
            </div>
        );
    }

    // ========================================
    // Render - Main
    // ========================================
    return (
        <div className="account-rider">
            {/* 페이지 헤더 - 협력사 사용자만 표시 */}
            {userType === 'SUPPLIER' && (
                <div className="page-header">
                    <div className="page-title-container">
                        <h1 className="page-title">출금제한</h1>
                    </div>
                </div>
            )}

            {/* 검색 필터 영역 */}
            <FilterSection
                userType={userType}
                searchPartnerId={searchPartnerId}
                searchRiderName={searchRiderName}
                searchRiderId={searchRiderId}
                searchContractStatus={searchContractStatus}
                searchWithdrawalStatus={searchWithdrawalStatus}
                partnerNames={partnerNames}
                contractStatusCodes={contractStatusCodes}
                contractStatusLoading={contractStatusLoading}
                loading={loading}
                onPartnerIdChange={setSearchPartnerId}
                onRiderNameChange={setSearchRiderName}
                onRiderIdChange={setSearchRiderId}
                onContractStatusChange={setSearchContractStatus}
                onWithdrawalStatusChange={setSearchWithdrawalStatus}
                onSearch={handleSearchWithReset}
            />

            {/* 테이블 영역 */}
            <div className="card">
                <div className="card-body">
                    <div className="table-container">
                        <table className="table sortable-table">
                            <thead>
                                <tr>
                                    {TABLE_HEADERS.map((header) => {
                                        const isSorted = sortConfig.columnKey === header.key;
                                        const direction = isSorted ? sortConfig.direction : null;
                                        return (
                                            <th
                                                key={header.key}
                                                className={`${header.sortable ? 'sortable-header' : ''} ${isSorted ? 'sorted' : ''}`}
                                                onClick={() => header.sortable && handleSort(header.key)}
                                            >
                                                {header.label}
                                                {header.sortable && <SortIcon direction={direction} />}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {!currentPageData || currentPageData.length === 0 ? (
                                    <tr>
                                        <td colSpan={TABLE_HEADERS.length} className="text-center">
                                            등록된 라이더가 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    currentPageData.map((rider, index) => (
                                        <RiderTableRow
                                            key={`${rider.riderId}-${index}`}
                                            rider={rider}
                                            index={index}
                                            isTerminatedContract={isRiderAccountDisabled(rider.contStatus)}
                                            getRiderRowClassName={getRiderRowClassName}
                                            getRiderContractStatusBadgeClass={getRiderContractStatusBadgeClass}
                                            getRiderContractStatusBadge={getRiderContractStatusBadge}
                                            getPreAccountBadgeClass={getPreAccountBadgeClass}
                                            getPreAccountBadgeText={getPreAccountBadgeText}
                                            formatNumber={formatNumber}
                                            onSendMoney={handleSendMoney}
                                            onWithdrawalLimit={handleWithdrawalLimit}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                maxPagesToShow={5}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RiderAccountingPage;
